'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Phone, Lock, Clock, Star, Copy, Check } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { loginWithPhone, saveUserSession } from '@/lib/auth';
import Icon from '@/components/ui/AppIcon';


interface LoginForm {
  phone: string;
  password: string;
}

const demoCredentials = [
  { role: 'ลูกค้า', label: 'Customer', phone: '0812345678', password: 'demo1234', color: 'bg-pink-50 text-primary border-primary/20' },
  { role: 'ผู้ขาย', label: 'Seller', phone: '0823456789', password: 'demo1234', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { role: 'แอดมิน', label: 'Admin', phone: '0834567890', password: 'demo1234', color: 'bg-purple-50 text-purple-700 border-purple-200' },
];

export default function LoginPageClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setServerError('');
    const { user, error } = await loginWithPhone(data.phone, data.password);
    setIsLoading(false);

    if (error || !user) {
      setServerError('เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง — ใช้บัญชีทดสอบด้านล่างเพื่อเข้าสู่ระบบ');
      return;
    }

    saveUserSession(user);

    if (user.role === 'admin') {
      router.push('/item-listing-browse-screen?role=admin');
    } else if (user.role === 'seller') {
      router.push('/item-listing-browse-screen?role=seller');
    } else {
      router.push('/item-listing-browse-screen?role=customer');
    }
  };

  const autofill = (phone: string, password: string) => {
    setValue('phone', phone);
    setValue('password', password);
  };

  const copyToClipboard = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-pink-gold flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-white blur-2xl" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo size={48} />
            <span className="text-white text-2xl font-bold tracking-tight">GOVTE</span>
          </div>
        </div>

        <div className="relative z-10 text-white">
          <h1 className="text-4xl font-bold leading-tight mb-4">
            หาเพื่อนเที่ยว<br />
            <span className="text-accent font-extrabold">คิดเป็นรายชั่วโมง</span>
          </h1>
          <p className="text-white/80 text-lg leading-relaxed mb-8">
            ระบบแชทเรียลไทม์ นับเวลาอัตโนมัติ<br />
            จ่ายเงินปลอดภัย
          </p>

          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: Clock, label: 'นับเวลา', sub: 'อัตโนมัติ' },
              { icon: Star, label: 'ปลอดภัย', sub: '100%' },
              { icon: Phone, label: 'แชท', sub: 'เรียลไทม์' },
            ].map((item) => (
              <div key={`feature-${item.label}`} className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <item.icon className="w-6 h-6 mx-auto mb-2 text-white" />
                <div className="text-white font-semibold text-sm">{item.label}</div>
                <div className="text-white/70 text-xs">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-white/60 text-sm">
          © 2026 GOVTE. All rights reserved.
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center p-5 sm:p-8 lg:p-12 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo + brand banner */}
          <div className="lg:hidden mb-6">
            <div className="flex items-center gap-3 mb-4">
              <AppLogo size={40} />
              <span className="text-foreground text-xl font-bold">GOVTE</span>
            </div>
            <div className="bg-gradient-pink-gold rounded-2xl p-4 flex items-center gap-4">
              <div className="flex-1 text-white">
                <div className="text-sm font-bold mb-0.5">เช่าของที่คุณต้องการ</div>
                <div className="text-xs text-white/80">รายชั่วโมง · แชทเรียลไทม์ · ปลอดภัย</div>
              </div>
              <div className="flex gap-2">
                {[Clock, Star, Phone].map((Icon, i) => (
                  <div key={i} className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">เข้าสู่ระบบ</h2>
            <p className="text-muted-foreground text-sm">ใส่เบอร์โทรและรหัสผ่านเพื่อดำเนินการต่อ</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
            {/* Phone */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                เบอร์โทรศัพท์
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  {...register('phone', {
                    required: 'กรุณากรอกเบอร์โทรศัพท์',
                    pattern: { value: /^0[0-9]{9}$/, message: 'รูปแบบเบอร์โทรไม่ถูกต้อง' }
                  })}
                  type="tel"
                  placeholder="0812345678"
                  className="w-full pl-10 pr-4 py-3 bg-input border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-150 text-sm"
                />
              </div>
              {errors.phone && (
                <p className="mt-1.5 text-xs text-red-500">{errors.phone.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">
                รหัสผ่าน
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  {...register('password', {
                    required: 'กรุณากรอกรหัสผ่าน',
                    minLength: { value: 6, message: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-11 py-3 bg-input border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-150 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Server error */}
            {serverError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-xs">{serverError}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-pink-gold text-white font-600 rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              style={{ minHeight: '48px' }}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  กำลังเข้าสู่ระบบ...
                </>
              ) : (
                'เข้าสู่ระบบ'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 sm:mt-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium px-2">บัญชีทดสอบ</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-2">
              {demoCredentials.map((cred) => (
                <div
                  key={`cred-${cred.label}`}
                  className={`border rounded-xl p-3 flex items-center justify-between ${cred.color}`}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-white/60 border whitespace-nowrap">
                      {cred.role}
                    </span>
                    <div>
                      <div className="text-xs font-600">{cred.phone}</div>
                      <div className="text-xs opacity-70">{cred.password}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(cred.phone, `phone-${cred.label}`)}
                      className="p-1.5 rounded-lg hover:bg-white/40 transition-colors"
                      title="คัดลอกเบอร์"
                    >
                      {copiedField === `phone-${cred.label}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => autofill(cred.phone, cred.password)}
                      className="text-xs font-600 px-2.5 sm:px-3 py-1.5 bg-white/60 rounded-lg hover:bg-white/80 transition-colors whitespace-nowrap"
                    >
                      ใช้
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}